import React, {useState, useEffect} from "react"
import {Box, Link, Typography, Accordion, AccordionSummary, AccordionDetails} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import NorthEastIcon from "@mui/icons-material/NorthEast"
import {Link as RouterLink} from "react-router-dom"

import {ConversationDetail} from "./ConversationDetail"
import {ThreadSelector} from "./ThreadSelector"

import {api} from "@indebted/api"
import type {CaseConversationSummary} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {colors} from "@indebted/theme/colors"

function ConversationSection({
	customerID,
	caseID,
	caseLabelByID,
}: {
	customerID: string
	caseID: string
	caseLabelByID: Record<string, string>
}) {
	const {notification} = useNotification()
	const [summaries, setSummaries] = useState<CaseConversationSummary[]>([])
	const [loading, setLoading] = useState(true)
	const [expandedOnce, setExpandedOnce] = useState(false)
	const [selectedConversationID, setSelectedConversationID] = useState<string | null>(null)

	useEffect(() => {
		let active = true
		setLoading(true)
		// Drop any thread picked for the previous case; otherwise effectiveID below
		// would keep resolving to a conversation that belongs to the old case.
		setSelectedConversationID(null)
		api.customer
			.getCaseConversations(customerID, caseID)
			.then((conversations) => {
				if (!active) return
				setSummaries(conversations)
			})
			.catch(() => {
				if (!active) return
				notification.error("Failed to load conversations")
				setSummaries([])
			})
			.finally(() => {
				if (!active) return
				setLoading(false)
			})
		return () => {
			active = false
		}
	}, [customerID, caseID, notification])

	// The case carries no conversation data of its own, so the section stays hidden
	// until the eager load confirms the case actually has linked conversations —
	// cases without inbound comms show no empty Conversation accordion.
	if (loading || summaries.length === 0) {
		return null
	}

	const count = summaries.length
	const effectiveID = selectedConversationID ?? summaries[0].id
	const otherCaseIds = summaries.find((s) => s.id === effectiveID)?.otherCaseIds ?? []

	return (
		<Accordion
			disableGutters
			elevation={0}
			onChange={(_, expanded) => {
				if (expanded) setExpandedOnce(true)
			}}
			sx={{border: `1px solid ${colors.gray["200"]}`, borderRadius: "4px", "&::before": {display: "none"}}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body2" color="textSecondary">
					Conversations · {count} thread{count === 1 ? "" : "s"} in this case
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{pt: 0}}>
				{expandedOnce && (
					<>
						{count > 1 && (
							<ThreadSelector
								summaries={summaries}
								selectedConversationID={effectiveID}
								onSelect={setSelectedConversationID}
							/>
						)}
						{otherCaseIds.length > 0 && (
							<Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
								<Typography variant="caption" color="textSecondary">
									Also on this customer:
								</Typography>
								{otherCaseIds.map((id) => (
									<Link
										key={id}
										component={RouterLink}
										to={`/customers/${customerID}/cases?CaseID=${id}`}
										variant="caption"
										underline="hover"
										sx={{display: "inline-flex", alignItems: "center", gap: 0.25}}
									>
										{caseLabelByID[id] ?? "Case"}
										<NorthEastIcon sx={{fontSize: 12}} />
									</Link>
								))}
							</Box>
						)}
						<ConversationDetail key={effectiveID} customerID={customerID} conversationID={effectiveID} />
					</>
				)}
			</AccordionDetails>
		</Accordion>
	)
}

export {ConversationSection}
